import { useAtomValue } from 'jotai';
import { useMemo } from 'react';
import { moduleFilterAtom, workerFilterAtom } from '../atoms';
import { useAvailableModulesByWorker } from './useAvailableModulesByWorker';
import { modules as defaultModules } from './modules';
import { useModulesQuery } from './useModulesQuery';
import { groupModulesByVersion } from './groupModulesByVersion';

export const useModules = () => {
  const filterText = useAtomValue(moduleFilterAtom);
  const selectedWorker = useAtomValue(workerFilterAtom);

  const {
    data: modules = defaultModules,
    isLoading,
    error,
  } = useModulesQuery();

  const { data: availableModulesByWorker = {} } =
    useAvailableModulesByWorker();

  const filteredModules = useMemo(() => {
    if (!filterText.trim()) {
      return modules;
    }

    const searchTextLowerCase = filterText.toLowerCase();
    return modules.filter((module) => {
      const nameMatches = module.name
        .toLowerCase()
        .includes(searchTextLowerCase);
      const descriptionMatches = module.description
        ?.toLowerCase()
        .includes(searchTextLowerCase);

      return nameMatches || descriptionMatches;
    });
  }, [modules, filterText]);
  const systemModules = useMemo(
    () => filteredModules.filter((module) => module.isSystem),
    [filteredModules]
  );

  const nonSystemModules = useMemo(() => {
    const customModules = filteredModules.filter(
      (module) => !module.isSystem
    );

    if (!selectedWorker) {
      return customModules;
    }

    const workerModuleIds = new Set(
      (availableModulesByWorker[selectedWorker] || []).map(
        (module) => module.id
      )
    );
    return customModules.filter((module) => workerModuleIds.has(module.id));
  }, [filteredModules, selectedWorker, availableModulesByWorker]);

  const systemModuleGroups = useMemo(
    () => groupModulesByVersion(systemModules),
    [systemModules]
  );

  const nonSystemModuleGroups = useMemo(
    () => groupModulesByVersion(nonSystemModules),
    [nonSystemModules]
  );

  return {
    error,
    isLoading,
    nonSystemModules: nonSystemModuleGroups,
    systemModules: systemModuleGroups,
  };
};
